import { useEffect, useMemo, useState } from 'react';
import { Avatar, Pill, formatDkk, type PillVariant } from '@jywer/ui';
import { csvDownloadUrl, fetchEntries, fetchGroups } from './api';
import { CommissionEntryRowDetail } from './CommissionEntryRowDetail';
import {
  defaultPeriodLabel,
  formatPeriodDanish,
  shiftPeriod,
  type CommissionEntryRow,
  type CommissionFilters,
  type CommissionGroupRow,
  type CommissionStatus,
  type PeriodInfo,
} from './types';

const STATUS_LABEL: Record<CommissionStatus, string> = {
  pending: 'Afventer',
  approved: 'Godkendt',
  paid: 'Udbetalt',
  reversed: 'Tilbageført',
};

const ENTITY_TYPE_LABEL: Record<'project' | 'subscription', string> = {
  project: 'Projekt',
  subscription: 'Abonnement',
};

export function CommissionsPage() {
  const [filters, setFilters] = useState<CommissionFilters>(() => ({
    period: defaultPeriodLabel(),
    status: ['pending'],
    salesRepIds: [],
    entityTypes: [],
  }));
  const [period, setPeriod] = useState<PeriodInfo | null>(null);
  const [groups, setGroups] = useState<CommissionGroupRow[]>([]);
  const [entriesByRep, setEntriesByRep] = useState<Map<string, CommissionEntryRow[]>>(
    () => new Map(),
  );
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [g, e] = await Promise.all([fetchGroups(filters), fetchEntries(filters)]);
        if (cancelled) return;
        setPeriod(g.period);
        setGroups(g.groups);
        const byRep = new Map<string, CommissionEntryRow[]>();
        for (const entry of e.entries) {
          const list = byRep.get(entry.sales_rep_id) ?? [];
          list.push(entry);
          byRep.set(entry.sales_rep_id, list);
        }
        setEntriesByRep(byRep);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Kunne ikke hente kommissioner');
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filters]);

  const allReps = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups) {
      if (g.sales_rep_name) map.set(g.sales_rep_id, g.sales_rep_name);
    }
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name, 'da'),
    );
  }, [groups]);

  const toggleStatus = (s: CommissionStatus) => {
    setFilters((f) => ({
      ...f,
      status: f.status.includes(s) ? f.status.filter((x) => x !== s) : [...f.status, s],
    }));
  };

  const toggleRep = (id: string) => {
    setFilters((f) => ({
      ...f,
      salesRepIds: f.salesRepIds.includes(id)
        ? f.salesRepIds.filter((x) => x !== id)
        : [...f.salesRepIds, id],
    }));
  };

  const toggleEntity = (t: 'project' | 'subscription') => {
    setFilters((f) => ({
      ...f,
      entityTypes: f.entityTypes.includes(t)
        ? f.entityTypes.filter((x) => x !== t)
        : [...f.entityTypes, t],
    }));
  };

  const applyEntryUpdate = (
    updater: (list: CommissionEntryRow[]) => CommissionEntryRow[],
    salesRepId: string,
  ) => {
    setEntriesByRep((prev) => {
      const next = new Map(prev);
      const list = next.get(salesRepId) ?? [];
      const updated = updater(list);
      next.set(salesRepId, updated);
      return next;
    });
  };

  const recomputeGroup = (salesRepId: string, entries: CommissionEntryRow[]) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.sales_rep_id !== salesRepId) return g;
        let totalCalculated = 0;
        let totalOverride = 0;
        let totalApproved = 0;
        let allApproved = true;
        let anyPending = false;
        for (const e of entries) {
          totalCalculated += Number(e.calculated_amount);
          if (e.override_amount !== null) totalOverride += Number(e.override_amount);
          if (e.approved_amount !== null) totalApproved += Number(e.approved_amount);
          if (e.status !== 'approved' && e.status !== 'paid') allApproved = false;
          if (e.status === 'pending') anyPending = true;
        }
        return {
          ...g,
          entry_count: entries.length,
          total_calculated: totalCalculated.toFixed(2),
          total_override: totalOverride.toFixed(2),
          total_approved: totalApproved.toFixed(2),
          all_approved: allApproved,
          any_pending: anyPending,
        };
      }),
    );
  };

  const handleEntryUpdated = (updated: CommissionEntryRow) => {
    applyEntryUpdate(
      (list) => list.map((e) => (e.id === updated.id ? updated : e)),
      updated.sales_rep_id,
    );
    const current = (entriesByRep.get(updated.sales_rep_id) ?? []).map((e) =>
      e.id === updated.id ? updated : e,
    );
    recomputeGroup(updated.sales_rep_id, current);
  };

  const handleEntryReversed = (original: CommissionEntryRow, reversal: CommissionEntryRow) => {
    applyEntryUpdate(
      (list) => [...list.map((e) => (e.id === original.id ? original : e)), reversal],
      original.sales_rep_id,
    );
    const current = [
      ...(entriesByRep.get(original.sales_rep_id) ?? []).map((e) =>
        e.id === original.id ? original : e,
      ),
      reversal,
    ];
    recomputeGroup(original.sales_rep_id, current);
  };

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold text-primary">Kommissioner</h1>

      <div className="flex flex-wrap items-center gap-2 rounded border border-line-1 bg-paper-0 p-3">
        <button
          type="button"
          onClick={() => setFilters((f) => ({ ...f, period: shiftPeriod(f.period, -1) }))}
          className="rounded border border-line-2 px-2 py-1 text-sm hover:border-primary"
          aria-label="Forrige periode"
        >
          ←
        </button>
        <span className="text-sm font-medium text-ink-1">
          {period ? formatPeriodDanish(period) : filters.period}
        </span>
        <button
          type="button"
          onClick={() => setFilters((f) => ({ ...f, period: shiftPeriod(f.period, 1) }))}
          className="rounded border border-line-2 px-2 py-1 text-sm hover:border-primary"
          aria-label="Næste periode"
        >
          →
        </button>

        <span className="mx-2 text-ink-3">|</span>

        <span className="text-xs font-semibold uppercase tracking-wide text-ink-2">Status</span>
        {(Object.keys(STATUS_LABEL) as CommissionStatus[]).map((s) => (
          <FilterChip
            key={s}
            active={filters.status.includes(s)}
            onClick={() => toggleStatus(s)}
            label={STATUS_LABEL[s]}
          />
        ))}

        <span className="mx-2 text-ink-3">|</span>

        {(['project', 'subscription'] as const).map((t) => (
          <FilterChip
            key={t}
            active={filters.entityTypes.includes(t)}
            onClick={() => toggleEntity(t)}
            label={ENTITY_TYPE_LABEL[t]}
          />
        ))}

        {allReps.length > 0 ? (
          <>
            <span className="mx-2 text-ink-3">|</span>
            <span className="text-xs font-semibold uppercase tracking-wide text-ink-2">Sælger</span>
            {allReps.map((r) => (
              <FilterChip
                key={r.id}
                active={filters.salesRepIds.includes(r.id)}
                onClick={() => toggleRep(r.id)}
                label={r.name}
              />
            ))}
          </>
        ) : null}

        <span className="ml-auto">
          <a
            href={csvDownloadUrl(filters)}
            className="rounded border border-line-2 bg-paper-0 px-3 py-1.5 text-sm hover:border-primary"
          >
            Eksporter CSV
          </a>
        </span>
      </div>

      {error ? (
        <div className="rounded border border-status-err/40 bg-status-err/5 px-4 py-3 text-sm text-status-err">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-ink-2">Henter kommissioner…</p>
      ) : groups.length === 0 ? (
        <p className="rounded border border-line-1 bg-paper-0 p-6 text-center text-sm text-ink-2">
          Ingen kommission-poster i denne periode.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {groups.map((g) => {
            const isOpen = expanded.has(g.sales_rep_id);
            const entries = entriesByRep.get(g.sales_rep_id) ?? [];
            const groupPill: { variant: PillVariant; label: string } = g.all_approved
              ? { variant: 'success', label: 'Alle godkendt' }
              : g.any_pending
                ? { variant: 'warning', label: 'Afventer' }
                : { variant: 'info', label: 'Delvis' };
            return (
              <div
                key={g.sales_rep_id}
                className="overflow-hidden rounded border border-line-1 bg-paper-0"
              >
                <button
                  type="button"
                  onClick={() =>
                    setExpanded((prev) => {
                      const next = new Set(prev);
                      if (next.has(g.sales_rep_id)) next.delete(g.sales_rep_id);
                      else next.add(g.sales_rep_id);
                      return next;
                    })
                  }
                  className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-paper-2/40"
                >
                  <span className="text-ink-3" aria-hidden="true">
                    {isOpen ? '▾' : '▸'}
                  </span>
                  <Avatar name={g.sales_rep_name} />
                  <span className="font-medium text-primary">{g.sales_rep_name ?? '(ukendt)'}</span>
                  <span className="text-sm text-ink-2">{g.entry_count} poster</span>
                  <Pill variant={groupPill.variant}>{groupPill.label}</Pill>
                  <span className="ml-auto flex items-center gap-4 text-sm">
                    <span className="tabular-nums text-ink-2">
                      Beregnet {formatDkk(g.total_calculated)}
                    </span>
                    {Number(g.total_override) !== 0 ? (
                      <span className="tabular-nums text-ink-2">
                        Override {formatDkk(g.total_override)}
                      </span>
                    ) : null}
                    <span className="tabular-nums font-semibold text-primary">
                      Godkendt {formatDkk(g.total_approved)}
                    </span>
                  </span>
                </button>

                {isOpen ? (
                  <div className="flex flex-col gap-2 border-t border-line-1 bg-paper-2/20 p-3">
                    {entries.map((e) => (
                      <CommissionEntryRowDetail
                        key={e.id}
                        entry={e}
                        onUpdated={handleEntryUpdated}
                        onReversed={handleEntryReversed}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-full border px-2.5 py-0.5 text-xs transition-colors ' +
        (active
          ? 'border-primary bg-primary text-white'
          : 'border-line-2 bg-paper-0 text-ink-1 hover:border-primary')
      }
    >
      {label}
    </button>
  );
}
